import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

interface AppLoaderState {
	// Core loading state
	isLoading: boolean;
	message: string;

	// Internal tracking
	_minTimeElapsed: boolean;
	_holdRequests: Set<string>;
	_initialLoaderHidden: boolean;
	_loaderStartTime: number;

	// Actions
	// eslint-disable-next-line no-unused-vars
	setLoading: (loading: boolean, message?: string) => void;
	// eslint-disable-next-line no-unused-vars
	setMessage: (message: string) => void;

	/**
	 * Request to hold the loader visible (e.g., while fetching critical data)
	 * @param key - Unique identifier for this hold request
	 */
	// eslint-disable-next-line no-unused-vars
	holdLoader: (key: string) => void;

	/**
	 * Release a hold request
	 * @param key - The same key used in holdLoader
	 */
	// eslint-disable-next-line no-unused-vars
	releaseLoader: (key: string) => void;

	/**
	 * Mark that minimum display time has elapsed
	 * (Called internally by the App component)
	 */
	_setMinTimeElapsed: () => void;

	/**
	 * Check if loader should be visible
	 * (Visible if: loading=true OR minTime not elapsed OR has hold requests)
	 */
	shouldShowLoader: () => boolean;

	/**
	 * Hide the initial HTML loader (called when React is ready)
	 * Respects minimum display time before hiding
	 */
	hideInitialLoader: () => void;

	/**
	 * Reset the loader state (for initial app load)
	 */
	reset: () => void;
}

// Minimum time to show the loader (prevents flicker)
export const MIN_LOADER_DISPLAY_MS = 1500;

// Track when the page started loading (for minimum display time calculation)
const pageLoadTime = Date.now();

const useAppLoaderStore = create<AppLoaderState>()(
	devtools(
		(set, get) => ({
			isLoading: true,
			message: 'Loading',
			_minTimeElapsed: false,
			_holdRequests: new Set(),
			_initialLoaderHidden: false,
			_loaderStartTime: pageLoadTime,

			setLoading: (loading, message) =>
				set(state => ({
					isLoading: loading,
					message: message ?? state.message,
				})),

			setMessage: message => set({ message }),

			holdLoader: key =>
				set(state => {
					const newHolds = new Set(state._holdRequests);
					newHolds.add(key);
					return { _holdRequests: newHolds };
				}),

			releaseLoader: key =>
				set(state => {
					const newHolds = new Set(state._holdRequests);
					newHolds.delete(key);
					return { _holdRequests: newHolds };
				}),

			_setMinTimeElapsed: () => set({ _minTimeElapsed: true }),

			shouldShowLoader: () => {
				const state = get();
				// Show loader if:
				// 1. Minimum time hasn't elapsed yet, OR
				// 2. There are active hold requests, OR
				// 3. isLoading is explicitly true
				return !state._minTimeElapsed || state._holdRequests.size > 0 || state.isLoading;
			},

			hideInitialLoader: () => {
				const state = get();
				if (state._initialLoaderHidden) return;

				const initialLoader = document.getElementById('initial-loader');
				if (!initialLoader) {
					set({ _initialLoaderHidden: true });
					return;
				}

				const elapsed = Date.now() - state._loaderStartTime;
				const remaining = Math.max(0, MIN_LOADER_DISPLAY_MS - elapsed);

				// Wait for minimum display time, then hide with fade animation
				setTimeout(() => {
					initialLoader.classList.add('hidden');
					// Remove from DOM after fade animation completes
					setTimeout(() => {
						initialLoader.remove();
					}, 300);
					set({ _initialLoaderHidden: true, _minTimeElapsed: true, isLoading: false });
				}, remaining);
			},

			reset: () =>
				set({
					isLoading: true,
					message: 'Loading',
					_minTimeElapsed: false,
					_holdRequests: new Set(),
					_initialLoaderHidden: false,
					_loaderStartTime: Date.now(),
				}),
		}),
		{ name: 'app-loader-store' }
	)
);

export default useAppLoaderStore;
